/* PARTNER DASHBOARD — every connected concrete supplier and their live jobs in one view.
   Polls /partner/dashboard every 15 s. Map reuses the contractor fleet map. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Factory, Handshake, AlertTriangle, Clock, MapPin, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { ContractorFleetMap } from "@/components/contractor/ContractorFleetMap";

const POLL_MS = 15_000;
const ROLLING = ["en_route", "on_site", "discharging", "returning", "loading", "slumping"];
const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null);

function Kpi({ label, value, accent }) {
  return (
    <div className="border border-[#22262E] bg-[#11141A] px-3 py-2 min-w-[110px]" data-testid={`partner-kpi-${label.toLowerCase().replace(/m³/g, "m3").replace(/\s/g, "-")}`}>
      <div className="text-[9px] tracking-[0.25em] text-[#8B909A] uppercase">{label}</div>
      <div className={`heading text-2xl font-black leading-none mt-0.5 ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

export default function PartnerSuppliers() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState(null);

  const load = useCallback(async () => {
    try { setData((await api.get("/partner/dashboard")).data); setErr(""); }
    catch (e) { setErr(e.response?.data?.detail || "Could not load your suppliers"); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);

  // The fleet map wants flat trucks — flatten every job's trucks and carry the job on each.
  const mapTrucks = useMemo(() => (data?.jobs || []).flatMap((j) => j.trucks.map((t) => ({
    id: t.truck_id, number: t.number, status: t.status, lat: t.lat, lng: t.lng, heading: t.heading,
    location_updated_at: t.location_updated_at, rego: "", driver_name: "",
    org: { name: j.supplier }, plant: null,
    job: { id: j.id, site_name: j.site_name, site_address: j.site_address, site_lat: j.site_lat, site_lng: j.site_lng, mix_code: j.mix_code, time_required: j.time_required, status: j.status, load_m3: t.load_m3 },
    eta: t.eta ? { to_site: t.eta.to_site, to_site_mins: t.eta.to_site_mins, back_to_plant: t.eta.back_to_plant, back_mins: t.eta.back_mins, source: t.eta.source } : null,
    today: { loads: 0, m3: 0 },
  }))), [data]);

  const s = data?.summary;
  const paused = (data?.suppliers || []).filter((x) => !x.sharing);

  return (
    <div className="h-full flex flex-col" data-testid="partner-suppliers-page">
      <div className="px-4 lg:px-6 pt-5 pb-3 space-y-3 border-b border-[#22262E] bg-[#0F1216]">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.25em] text-[#8B909A]">LIVE · REFRESHES EVERY 15 S · {data?.date}</div>
            <h1 className="heading text-2xl sm:text-3xl font-black uppercase text-white">My <span className="text-[#38BDF8]">Suppliers</span></h1>
          </div>
          <div className="flex items-center gap-2 text-[10px] mono text-[#8B909A]">
            {data?.generated_at && <span data-testid="partner-updated">Updated {new Date(data.generated_at).toLocaleTimeString()}</span>}
            <button onClick={load} data-testid="partner-refresh" className="inline-flex items-center gap-1 border border-[#2C313A] px-2 py-1 hover:border-[#38BDF8] hover:text-[#38BDF8]"><RefreshCw className="w-3 h-3" /> Refresh</button>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Kpi label="Suppliers" value={s?.suppliers ?? "—"} accent="text-[#38BDF8]" />
          <Kpi label="Jobs today" value={s?.jobs ?? "—"} />
          <Kpi label="Live jobs" value={s?.active_jobs ?? "—"} accent="text-emerald-400" />
          <Kpi label="Loads batched" value={s?.loads_batched ?? "—"} />
          <Kpi label="m³ delivered" value={s?.m3_today ?? "—"} />
          <Kpi label="Trucks rolling" value={s?.trucks_rolling ?? "—"} accent="text-amber-300" />
        </div>
        {paused.length > 0 && (
          <div className="border border-amber-600/60 bg-amber-950/20 text-amber-300 px-3 py-2 text-[12px] mono" data-testid="partner-paused-banner">
            <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5" />
            Sharing paused: {paused.map((p) => p.name).join(", ")} — their free sharing window has ended and they need a plan to resume.
          </div>
        )}
      </div>

      {err && <div className="m-4 p-3 border border-red-900 bg-red-950/30 text-red-300 text-sm" data-testid="partner-error">{err}</div>}

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        <div className="lg:w-[440px] lg:shrink-0 lg:border-r border-[#22262E] overflow-y-auto p-3 space-y-2 max-h-[45vh] lg:max-h-none" data-testid="partner-job-list">
          {data && (data.suppliers || []).length === 0 && (
            <div className="border border-dashed border-[#2C313A] p-6 text-center text-sm text-[#A1A6B0]" data-testid="partner-no-suppliers">
              <Handshake className="w-8 h-8 mx-auto mb-3 text-[#38BDF8]" />
              No suppliers connected yet. Send your invite link to the concrete plants you work with — when they
              connect, their loads and ETAs land here.
              <div className="mt-3"><Link to="/contractor/invite" data-testid="partner-invite-cta" className="inline-flex items-center gap-1 text-[#38BDF8] hover:underline">Get my invite link <ArrowRight className="w-3 h-3" /></Link></div>
            </div>
          )}
          {data && data.suppliers.length > 0 && data.jobs.length === 0 && (
            <div className="border border-dashed border-[#2C313A] p-6 text-center text-sm text-[#A1A6B0]" data-testid="partner-no-jobs">
              Nothing booked on your gear today across {data.suppliers.length} supplier(s).
            </div>
          )}
          {(data?.jobs || []).map((j) => (
            <div key={j.id} className="border border-[#22262E] bg-[#11141A]" data-testid={`partner-job-${j.id}`}>
              <div className="px-3 py-2 border-b border-[#22262E] flex items-center gap-2 flex-wrap">
                <span className="mono text-[10px] uppercase tracking-widest text-[#38BDF8] inline-flex items-center gap-1"><Factory className="w-3 h-3" />{j.supplier}</span>
                <span className="mono text-[11px] text-[#8B909A] inline-flex items-center gap-1"><Clock className="w-3 h-3" />{j.time_required || "—"}</span>
                <span className="ml-auto mono text-[11px] text-white font-black">{j.delivered_m3 || 0} / {j.quantity_m3 || 0} m³</span>
              </div>
              <div className="px-3 py-2">
                <div className="text-sm font-bold text-white truncate inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#38BDF8]" />{j.site_name || j.site_address || "Site"}</div>
                <div className="mono text-[11px] text-[#8B909A] truncate">{j.site_address}</div>
                <div className="mono text-[10px] uppercase tracking-widest text-[#A1A6B0] mt-1">
                  {j.mix_code || "—"} · {j.loads_batched} load{j.loads_batched === 1 ? "" : "s"} batched
                  {j.first_batched_at ? ` · first ${hhmm(j.first_batched_at)}` : ""}
                </div>
              </div>
              {j.trucks.length > 0 && (
                <div className="border-t border-[#22262E] divide-y divide-[#1b1f26]">
                  {j.trucks.map((t) => (
                    <button key={t.truck_id} type="button" onClick={() => setSel(t.truck_id)} data-testid={`partner-truck-${t.truck_id}`}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[#161A20] ${sel === t.truck_id ? "bg-[#38BDF8]/10" : ""}`}>
                      <span className="heading text-base font-black text-white">{t.number}</span>
                      <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${ROLLING.includes(t.status) ? "border-emerald-600 text-emerald-400" : "border-[#2C313A] text-[#8B909A]"}`}>{(t.status || "").replace(/_/g, " ")}</span>
                      {t.batched_at && <span className="mono text-[10px] text-[#8B909A]">batched {hhmm(t.batched_at)}</span>}
                      <span className="ml-auto mono text-[11px] text-right">
                        {t.eta?.to_site ? (
                          <span className="text-[#38BDF8] font-black">ETA {t.eta.to_site}{t.eta.to_site_mins != null ? ` · ${t.eta.to_site_mins}m` : ""}</span>
                        ) : t.distance_km != null ? <span className="text-[#8B909A]">{t.distance_km} km away</span> : <span className="text-[#4b5563]">no GPS</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex-1 min-h-[45vh] lg:min-h-0 relative" data-testid="partner-map">
          <ContractorFleetMap trucks={mapTrucks} plants={data?.plants || []} selectedId={sel} onSelect={setSel} />
        </div>
      </div>
    </div>
  );
}
